import { useState, useEffect, useMemo, useCallback } from "react";
import type { Step, CallBackProps, Status } from "react-joyride";
import { captureEvent } from "@/lib/analyticsQueue";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { TOUR_TARGETS, TOUR_START_DELAY_MS } from "./constants";

export function useOnboardingTour(pageReady = true) {
  const { profile, updateProfile } = useAuth();
  const { t, isRTL } = useLanguage();
  const [run, setRun] = useState(false);

  const shouldShowTour =
    !!profile && !profile.onboarding_completed && pageReady;

  useEffect(() => {
    if (!shouldShowTour) return;

    const timer = setTimeout(() => setRun(true), TOUR_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldShowTour]);

  const steps: Step[] = useMemo(
    () => [
      {
        target: TOUR_TARGETS.STUDIO_TABS,
        title: t.onboardingTour.studioTabsTitle,
        content: t.onboardingTour.studioTabsContent,
        disableBeacon: true,
      },
      {
        target: TOUR_TARGETS.SOURCE_UPLOAD,
        title: t.onboardingTour.sourceUploadTitle,
        content: t.onboardingTour.sourceUploadContent,
        disableBeacon: true,
      },
      {
        target: TOUR_TARGETS.PROMPT_AREA,
        title: t.onboardingTour.promptAreaTitle,
        content: t.onboardingTour.promptAreaContent,
        disableBeacon: true,
      },
      {
        target: TOUR_TARGETS.ENHANCE_PROMPT_BUTTON,
        title: t.onboardingTour.enhanceSuggestionsTitle,
        content: t.onboardingTour.enhanceSuggestionsContent,
        disableBeacon: true,
      },
      {
        target: TOUR_TARGETS.GENERATION_BAR,
        title: t.onboardingTour.generationBarTitle,
        content: t.onboardingTour.generationBarContent,
        disableBeacon: true,
        disableScrolling: true,
      },
      {
        target: TOUR_TARGETS.GENERATE_BUTTON,
        title: t.onboardingTour.generateButtonTitle,
        content: t.onboardingTour.generateButtonContent,
        disableBeacon: true,
        disableScrolling: true,
      },
      {
        target: TOUR_TARGETS.CREATIONS_GALLERY,
        title: t.onboardingTour.creationsGalleryTitle,
        content: t.onboardingTour.creationsGalleryContent,
        disableBeacon: true,
      },
    ],
    [t],
  );

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status } = data;
      if (status === "finished" || status === "skipped") {
        setRun(false);
        captureEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETE, { status: status as string });
        updateProfile({ onboarding_completed: true });
      }
    },
    [updateProfile],
  );

  return { run, steps, handleCallback, isRTL };
}
